import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Pencil, Wand2, RefreshCw, Check, Loader2, Sparkles } from 'lucide-react';
import api from '../services/api';

const ContentCard = ({
  vehicleId,
  label,
  value,
  field,
  maxChars,
  multiline,
  saveEndpoint,
  generateEndpoint,
  onVehicleUpdate,
  charWarning,
  charWarningLabel,
  readOnly,
  hashtags,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [refining, setRefining] = useState(false);
  const [showRefine, setShowRefine] = useState(false);
  const [refineInstruction, setRefineInstruction] = useState('');

  const autoSaveTimerRef = useRef(null);
  const refineInputRef = useRef(null);
  const editRef = useRef(null);

  // Sync editValue when value prop changes (e.g. after regenerate/refine)
  useEffect(() => {
    setEditValue(value || '');
  }, [value]);

  // Focus the refine input when panel is shown
  useEffect(() => {
    if (showRefine) {
      setTimeout(() => refineInputRef.current?.focus(), 50);
    }
  }, [showRefine]);

  // Focus the edit field when editing starts
  useEffect(() => {
    if (isEditing) {
      setTimeout(() => editRef.current?.focus(), 50);
    }
  }, [isEditing]);

  // --- Save logic ---
  const save = useCallback(async (textToSave) => {
    if (!saveEndpoint) return;
    setSaving(true);
    setSaved(false);
    try {
      const url = saveEndpoint.url.replace('{id}', vehicleId);
      const method = saveEndpoint.method || 'put';
      const body = { [saveEndpoint.bodyKey]: textToSave };
      const response = await api[method](url, body);
      if (onVehicleUpdate) onVehicleUpdate(response.data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('ContentCard save error:', err);
    } finally {
      setSaving(false);
    }
  }, [vehicleId, saveEndpoint, onVehicleUpdate]);

  // Auto-save with debounce
  useEffect(() => {
    if (!isEditing) return;
    if (editValue === (value || '')) return;

    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      save(editValue);
    }, 1500);

    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [editValue, isEditing, value, save]);

  // Save immediately on close
  const handleCloseEdit = () => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    if (editValue !== (value || '')) {
      save(editValue);
    }
    setIsEditing(false);
  };

  // --- Generate ---
  const handleGenerate = async () => {
    if (!generateEndpoint) return;
    setGenerating(true);
    try {
      const url = generateEndpoint.url.replace('{id}', vehicleId);
      const response = await api.post(url);
      if (onVehicleUpdate) onVehicleUpdate(response.data);
    } catch (err) {
      console.error('ContentCard generate error:', err);
    } finally {
      setGenerating(false);
    }
  };

  // --- AI Refine ---
  const handleRefine = async () => {
    if (!refineInstruction.trim()) return;
    setRefining(true);
    try {
      const response = await api.post(`/vehicles/${vehicleId}/refine-content`, {
        field,
        instruction: refineInstruction,
      });
      if (onVehicleUpdate) onVehicleUpdate(response.data);
      setRefineInstruction('');
      setShowRefine(false);
    } catch (err) {
      console.error('ContentCard refine error:', err);
    } finally {
      setRefining(false);
    }
  };

  // --- Character counter ---
  const charCount = (value || '').length;
  const charCountDisplay = maxChars ? `${charCount}/${maxChars}` : null;

  // --- Render content area ---
  const renderContent = () => {
    // Generating state
    if (generating) {
      return (
        <div className="py-6 text-center">
          <Loader2 className="h-6 w-6 animate-spin text-accent-purple mx-auto mb-2" />
          <p className="text-sm text-muted">Génération en cours...</p>
        </div>
      );
    }

    // Refining state
    if (refining) {
      return (
        <div className="py-6 text-center">
          <Loader2 className="h-6 w-6 animate-spin text-accent-purple mx-auto mb-2" />
          <p className="text-sm text-muted">Modification en cours...</p>
        </div>
      );
    }

    // Editing state
    if (isEditing) {
      return (
        <div>
          {multiline ? (
            <textarea
              ref={editRef}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              rows={6}
              className="w-full px-3 py-2 bg-dark-input border border-accent-purple/50 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-purple text-primary text-sm resize-vertical"
            />
          ) : (
            <input
              ref={editRef}
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="w-full px-3 py-2 bg-dark-input border border-accent-purple/50 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-purple text-primary text-sm"
            />
          )}
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-secondary">
              {saving && (
                <span className="inline-flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Sauvegarde...
                </span>
              )}
              {saved && (
                <span className="text-green-400 inline-flex items-center gap-0.5">
                  <Check className="h-3 w-3" />
                  Sauvegardé
                </span>
              )}
              {!saving && !saved && editValue !== (value || '') && 'Sauvegarde auto activée'}
            </span>
            <button
              onClick={handleCloseEdit}
              className="text-sm text-secondary hover:text-primary px-3 py-1"
            >
              Fermer
            </button>
          </div>
        </div>
      );
    }

    // Empty state
    if (!value) {
      return (
        <p className="text-sm italic text-muted py-2">Aucun contenu généré</p>
      );
    }

    // Display state with optional charWarning threshold line
    if (charWarning && value.length > charWarning) {
      const before = value.slice(0, charWarning);
      const after = value.slice(charWarning);
      return (
        <div className="bg-dark-hover rounded-md p-4">
          <p className="whitespace-pre-wrap text-label text-sm leading-relaxed">{before}</p>
          <div className="my-2 border-t border-dashed border-amber-500/60 relative">
            {charWarningLabel && (
              <span className="absolute -top-2.5 left-2 bg-dark-hover px-1.5 text-[10px] font-medium text-amber-400">
                {charWarningLabel}
              </span>
            )}
          </div>
          <p className="whitespace-pre-wrap text-muted text-sm leading-relaxed">{after}</p>
        </div>
      );
    }

    return (
      <div className="bg-dark-hover rounded-md p-4">
        <p className="whitespace-pre-wrap text-label text-sm leading-relaxed">{value}</p>
      </div>
    );
  };

  return (
    <div className="bg-dark-card rounded-lg p-5">
      {/* Header row */}
      <div className="flex items-center justify-between mb-3">
        {/* Left: icon + label + char counter */}
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="h-4 w-4 text-accent-purple flex-shrink-0" />
          <span className="text-sm font-semibold text-primary truncate">{label}</span>
          {charCountDisplay && (
            <span className={`text-xs flex-shrink-0 ${maxChars && charCount > maxChars ? 'text-red-400' : 'text-muted'}`}>
              {charCountDisplay}
            </span>
          )}
        </div>

        {/* Right: save indicator + action buttons */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {/* Saving indicator (outside edit mode, in header) */}
          {!isEditing && saving && (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted" />
          )}
          {!isEditing && saved && (
            <Check className="h-3.5 w-3.5 text-green-400" />
          )}

          {/* Edit button */}
          {!readOnly && value && !isEditing && !generating && !refining && (
            <button
              onClick={() => {
                setEditValue(value);
                setIsEditing(true);
                setShowRefine(false);
              }}
              className="p-1.5 text-muted hover:text-primary hover:bg-dark-hover rounded-md transition-colors"
              title="Modifier manuellement"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          )}

          {/* AI Refine button (wand) */}
          {!readOnly && value && !isEditing && !generating && !refining && (
            <button
              onClick={() => setShowRefine(!showRefine)}
              className={`p-1.5 rounded-md transition-colors ${
                showRefine
                  ? 'text-accent-purple bg-accent-purple/10'
                  : 'text-muted hover:text-accent-purple hover:bg-dark-hover'
              }`}
              title="Ajuster avec l'IA"
            >
              <Wand2 className="h-3.5 w-3.5" />
            </button>
          )}

          {/* Regenerate button */}
          {!readOnly && generateEndpoint && !isEditing && !generating && !refining && (
            <button
              onClick={handleGenerate}
              className="p-1.5 text-muted hover:text-primary hover:bg-dark-hover rounded-md transition-colors"
              title="Régénérer"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Content area */}
      {renderContent()}

      {/* Hashtags */}
      {hashtags && hashtags.length > 0 && !isEditing && !generating && !refining && (
        <div className="flex flex-wrap gap-2 mt-3">
          {hashtags.map((tag, i) => (
            <span key={i} className="text-sm text-accent-purple">
              {tag.startsWith('#') ? tag : `#${tag}`}
            </span>
          ))}
        </div>
      )}

      {/* AI Refine panel */}
      {showRefine && !isEditing && !generating && !refining && (
        <div className="mt-3 p-3 bg-accent-purple/10 border border-accent-purple/20 rounded-md">
          <label className="block text-sm font-medium text-accent-purple-light mb-2">
            Dites à l'IA ce qu'il faut changer :
          </label>
          <div className="flex gap-2">
            <input
              ref={refineInputRef}
              type="text"
              value={refineInstruction}
              onChange={(e) => setRefineInstruction(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleRefine()}
              placeholder="Ex: Rends le texte plus court..."
              className="flex-1 px-3 py-2 text-sm bg-dark-input border border-accent-purple/30 rounded-md text-primary placeholder-muted focus:outline-none focus:ring-2 focus:ring-accent-purple"
            />
            <button
              onClick={handleRefine}
              disabled={!refineInstruction.trim()}
              className="inline-flex items-center px-3 py-2 text-sm bg-accent-purple text-white rounded-md hover:bg-accent-purple-dark disabled:opacity-50"
            >
              <Wand2 className="h-3.5 w-3.5 mr-1" />
              Appliquer
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentCard;
